import { FC, ReactElement, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import AOS from "aos";

import "aos/dist/aos.css";

import Routes from "@/routes";
import { Splash } from "@/pages";
import { ScrollToTop, SnackbarMessage } from "@/components";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PostHogAppProvider } from "@/providers/PostHogProvider";

import { MessageContainer } from "@/context/message-context";
import { ModalProvider } from "@/context/modal-context";
import { HTSDescriptionsProvider } from "@/context/hts-descriptions-context";
import { useGetCompanyInfoQuery, useGetUserFromTokenQuery, useRefreshSessionMutation } from "@/services";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

const App: FC = (): ReactElement => {
  const { accessToken, isUserLoaded, user, tokenObtainedAt } = useAppSelector((state) => state.auth);

  useEffect(() => {
    AOS.init();
  }, []);

  const [refreshSession] = useRefreshSessionMutation();

  // Auto-refresh token every 50 minutes
  useEffect(() => {
    if (!accessToken) return;

    const refresh = () => {
      if (tokenObtainedAt && Date.now() - tokenObtainedAt < 5 * 60 * 1000) return;
      const email = user?.email;
      if (email) {
        refreshSession({ email });
      }
    };

    // Refresh immediately on mount (token may be near expiry), then every 50 min
    refresh();
    const interval = setInterval(refresh, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accessToken, user?.email, refreshSession, tokenObtainedAt]);

  const { isLoading } = useGetUserFromTokenQuery(undefined, {
    skip: !accessToken,
    refetchOnMountOrArgChange: true,
  });

  const isAdmin = user?.userRole === roles.TDADMIN;
  const { isLoading: companyLoading } = useGetCompanyInfoQuery(undefined, {
    // Wait until user is loaded; skip for TD Admin entirely (requires explicit tenant selection later)
    skip: !accessToken || !isUserLoaded || isAdmin,
  });
  if (isLoading || companyLoading || (accessToken && !isUserLoaded)) {
    return <Splash />;
  }

  return (
    <MessageContainer>
      <ModalProvider>
        <HTSDescriptionsProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <PostHogAppProvider>
                <ScrollToTop />
                <Routes />
              </PostHogAppProvider>
            </ErrorBoundary>
          </BrowserRouter>
          <SnackbarMessage />
        </HTSDescriptionsProvider>
      </ModalProvider>
    </MessageContainer>
  );
};

export default App;
